import React, { useEffect, useState, useRef, lazy, memo, createElement } from 'react';
import { CheckIcon, ChevronDownIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, StarIcon, ShieldCheckIcon, LockIcon, ZapIcon } from 'lucide-react';
import { Footer } from '../components/Footer';
// ─── Checkout URL builder ─────────────────────────────────────────────────────
const buildUrl = (template: string) => {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? template.replace('{{fbclid}}', fbclid) : template;
};
// ─── Tracking ─────────────────────────────────────────────────────────────────
declare global {
  interface Window {
    fbq: (action: string, event: string, params?: any) => void;
  }
}
let icFiredV4 = false;
const trackV4 = (name: string, price: number) => {
  if (typeof window.fbq !== 'function') return;
  window.fbq('track', 'AddToCart', {
    content_name: name,
    value: price,
    currency: 'USD'
  });
  if (!icFiredV4) {
    icFiredV4 = true;
    window.fbq('track', 'InitiateCheckout', {
      content_name: name,
      value: price,
      currency: 'USD'
    });
  }
};
// ─── Design tokens ────────────────────────────────────────────────────────────
const ACCENT = '#FF6C00';
const ACCENT_GLOW = 'rgba(255,108,0,0.3)';
const BG = '#0B0B0B';
const GOLD = '#D4AF37';
// ─── Checkout URLs ────────────────────────────────────────────────────────────
const CHECKOUT_DIGITAL = 'https://legendaryreads.com/cart/42231842701394:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_PREMIUM = 'https://legendaryreads.com/cart/42285346947154:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BUNDLE = 'https://legendaryreads.com/cart/42285352288338:1?checkout&fbclid={{fbclid}}';
const CHECKOUT_BASIC = 'https://legendaryreads.com/cart/42185022439506:1?checkout&fbclid={{fbclid}}';
const BASE_PRICE = 59.97;
const BUMP1_PRICE = 35;
const BUMP2_PRICE_STANDALONE = 220;
const BUMP2_PRICE_WITH_BUMP1 = 185;
const BUMP1_ITEMS = ['Everything in the Complete Digital Collection', 'Back In Time — Premium Hardcover Edition', 'Delivery in 12 days'];
const BUMP2_ITEMS = ['Everything in the Complete Digital Collection', 'All 4 volumes in Paperback Edition', 'Back In Time Audiobook Experience', 'Hill Valley 1985 Limited Vintage Trucker Hat', "Flux Energy Core Collector's Framed Print — 10×10 Limited Edition", 'Time Machine Heritage Tote', '40 Years Travelling Through Time Commemorative Mug', 'Fast Delivery'];
const BUNDLE_IMAGES = ['https://cdn.magicpatterns.com/uploads/2mCBcB1vUDxS6qefycewPD/TIME_TRAVELLERS_COLLECTOR_BUNDLED.png', 'https://cdn.magicpatterns.com/uploads/7BQJLvNZKyzy2Jn4rjWLvk/bit_audiobook.png', 'https://cdn.magicpatterns.com/uploads/8av42GqLUN9WRwXAhxHRBi/Generated_Image_November_18,_2025_-_3_14PM.png', 'https://uploadthingy.s3.us-west-1.amazonaws.com/i3DKchuX8RMmzYrqAhpK13/foam-trucker-hat-black-one-size-right-front-6914c3a4781f3.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/rbwPKtwrCHTaFNqFeaH2fA/enhanced-matte-paper-framed-poster-%28in%29-white-10x10-front-6914bc8d8b378.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/2eQBo88LwYLe7TLxEHMDQe/white-glossy-mug-white-11-oz-handle-on-left-6914d9d1888e4.jpg', 'https://uploadthingy.s3.us-west-1.amazonaws.com/dsPxuWW7a4Xja3nWKMPU8M/all-over-print-tote-bag-yellow-15x15-mockup-69123afa061d2.jpg'];
// ─── Shared primitives ────────────────────────────────────────────────────────
const OrangeButton = ({
  href,
  onClick,
  children,
  small = false
}: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  small?: boolean;
}) => <a href={href} onClick={onClick} className="cta block w-full text-center font-bold tracking-wide rounded-lg transition-all duration-200 active:scale-[0.98] no-underline hover:brightness-110 shadow-lg" style={{
  minHeight: small ? '44px' : '56px',
  padding: small ? '10px 16px' : '16px 24px',
  fontSize: small ? '14px' : '17px',
  background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
  color: '#0B0B0B',
  fontFamily: 'Inter, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}} data-id="element-1013">
    {children}
  </a>;
const BumpItem = ({
  text
}: {
  text: string;
}) => <li className="flex items-start gap-2" data-id="element-1014">
    <span style={{
    color: ACCENT,
    flexShrink: 0,
    fontSize: '9px',
    marginTop: '5px'
  }} data-id="element-1015">
      ✦
    </span>
    <span className="text-xs leading-snug" style={{
    color: 'rgba(245,245,245,0.78)',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-1016">
      {text}
    </span>
  </li>;
// ─── Image Lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({
  images,
  startIndex,
  onClose
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => i === 0 ? images.length - 1 : i - 1);
  const next = () => setIdx(i => i === images.length - 1 ? 0 : i + 1);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose} data-id="element-1017">
      <div className="relative w-full max-w-lg" onClick={e => e.stopPropagation()} data-id="element-1018">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors" aria-label="Close" data-id="element-1019">
          <XIcon className="h-7 w-7" data-id="element-1020" />
        </button>
        <div className="rounded-xl overflow-hidden shadow-2xl bg-black" style={{
        aspectRatio: '1/1'
      }} data-id="element-1021">
          <img src={images[idx]} alt={`Item ${idx + 1}`} className="w-full h-full object-contain" decoding="async" loading="lazy" data-id="element-1022" />
        </div>
        <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Previous" data-id="element-1023">
          <ChevronLeftIcon className="h-5 w-5" data-id="element-1024" />
        </button>
        <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-all" aria-label="Next" data-id="element-1025">
          <ChevronRightIcon className="h-5 w-5" data-id="element-1026" />
        </button>
        <div className="flex justify-center gap-1.5 mt-3" data-id="element-1027">
          {images.map((_, i) => <button key={i} onClick={() => setIdx(i)} className="rounded-full transition-all duration-200" style={{
          width: i === idx ? '20px' : '8px',
          height: '8px',
          backgroundColor: i === idx ? ACCENT : 'rgba(255,255,255,0.3)'
        }} data-id="element-1028" />)}
        </div>
      </div>
    </div>;
};
// ─── A. STICKY ALERT BAR ─────────────────────────────────────────────────────
const AlertBar = () => <div className="w-full min-h-[32px] md:h-[36px] flex items-center justify-center px-3 text-center text-[7px] leading-tight md:text-sm font-bold tracking-wide sticky top-0 z-50 py-1 md:py-0 md:whitespace-nowrap overflow-hidden" style={{
  background: 'linear-gradient(90deg, #FF6C00 0%, #FFD300 100%)',
  color: '#0B0B0B'
}} data-id="element-1029">
    <span className="md:hidden" data-id="element-1030">
      40TH ANNIVERSARY OFFER — MAIN BOOK + 3 COMPANION BOOKS FREE
    </span>
    <span className="hidden md:inline" data-id="element-1031">
      40TH ANNIVERSARY LIMITED OFFER — BACK IN TIME MAIN BOOK + 3 COMPANION
      BOOKS FOR FREE
    </span>
  </div>;
// ─── HEADER ──────────────────────────────────────────────────────────────────
const HeaderV4 = () => <header className="bg-black text-white border-b border-orange-500/30 z-40 sticky top-[32px] md:top-[36px]" data-id="element-1032">
    <div className="container mx-auto px-4 py-2.5 flex justify-between items-center" data-id="element-1033">
      <span className="font-bold text-lg bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text font-future" data-id="element-1034">
        Back In Time
      </span>
      <a href="#pricing-section" className="cta font-bold text-sm px-4 py-1.5 rounded-md transition-all hover:brightness-110" style={{
      background: 'linear-gradient(90deg, #FFD300 0%, #FF6C00 100%)',
      color: '#0B0B0B',
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1035">
        Get My Copy
      </a>
    </div>
  </header>;
// ─── B. HERO SECTION ─────────────────────────────────────────────────────────
const HeroV4 = () => <section className="py-6 md:py-10 relative overflow-hidden" style={{
  backgroundColor: BG
}} data-id="element-1036">
    <div className="absolute inset-0 opacity-20" style={{
    background: 'radial-gradient(ellipse at 70% 50%, rgba(255,108,0,0.15) 0%, transparent 70%)'
  }} data-id="element-1037" />
    <div className="container mx-auto px-4 md:px-8 max-w-6xl relative z-10" data-id="element-1038">
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-1039">
        {/* Left: copy */}
        <div className="w-full md:w-1/2 text-center md:text-left" data-id="element-1040">
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
          color: ACCENT,
          fontFamily: 'Inter, sans-serif',
          letterSpacing: '0.12em'
        }} data-id="element-1041">
            40th Anniversary Collector's Edition
          </p>
          <h1 className="text-[28px] md:text-5xl font-bold leading-tight mb-4 text-white font-future tracking-tight" data-id="element-1042">
            You've Seen It 50 Times.{' '}
            <span className="bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1043">
              You've Never Read Anything Like This.
            </span>
          </h1>
          <p className="text-sm md:text-lg text-white/75 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1044">
            The only collector's edition that goes beyond the film — into the
            production truth, the cultural legacy, and the reason this story
            lives in your bones 40 years later. Available in Print or Digital.
            Yours to keep permanently.
          </p>

          {/* Mobile image */}
          <div className="md:hidden mb-5 flex justify-center" data-id="element-1045">
            <div className="relative w-[85%]" data-id="element-1046">
              <div className="absolute -inset-1 rounded-xl blur-md opacity-40" style={{
              background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
            }} data-id="element-1047" />
              <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
              aspectRatio: '1/1'
            }} data-id="element-1048">
                <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="400" height="400" data-id="element-1049" />
              </div>
            </div>
          </div>

          {/* Stars */}
          <div className="flex items-center gap-2 justify-center md:justify-start mb-5" data-id="element-1050">
            <div className="flex gap-0.5" data-id="element-1051">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1052" />)}
            </div>
            <span className="text-white font-bold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1053">
              4.9
            </span>
            <span className="text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1054">
              (5,354+ Verified Reviews)
            </span>
          </div>

          <div className="max-w-md mx-auto md:mx-0" data-id="element-1055">
            <OrangeButton href="#pricing-section" data-id="element-1056">
              GET THE COMPLETE COLLECTION →
            </OrangeButton>
          </div>

          <div className="flex items-center justify-center md:justify-start gap-4 mt-4" data-id="element-1057">
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1058">
              <ShieldCheckIcon className="h-4 w-4 text-green-400" data-id="element-1059" />
              Secure Checkout
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1060">
              <ZapIcon className="h-4 w-4 text-yellow-400" data-id="element-1061" />
              Instant Access
            </div>
            <div className="flex items-center gap-1.5 text-white/50 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1062">
              <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1063" />
              One-Time Payment
            </div>
          </div>
        </div>

        {/* Right: desktop image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-center" data-id="element-1064">
          <div className="relative w-full max-w-md" data-id="element-1065">
            <div className="absolute -inset-2 rounded-2xl blur-xl opacity-30" style={{
            background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
          }} data-id="element-1066" />
            <div className="relative rounded-2xl overflow-hidden border border-orange-500/30 shadow-[0_0_60px_rgba(255,108,0,0.2)]" style={{
            aspectRatio: '1/1'
          }} data-id="element-1067">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" fetchpriority="high" decoding="async" width="600" height="600" data-id="element-1068" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── C. IDENTIFICATION BLOCK ─────────────────────────────────────────────────
const IdentificationV4 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1069">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1070">
      <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8 font-future tracking-wide" data-id="element-1071">
        This Is For You If...
      </h2>

      <div className="space-y-4 mb-8" data-id="element-1072">
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1073">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1074">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1075">
            You still get chills when the lightning hits the clock tower — and
            you can't fully explain why to anyone who wasn't there
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1076">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1077">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1078">
            You've tried to show this film to your kids and realized you didn't
            have the words to explain why it's different from everything made
            since
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1079">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1080">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1081">
            You've bought anniversary content before — and walked away feeling
            like the legacy deserved better
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(34,197,94,0.07)',
        border: '1px solid rgba(34,197,94,0.2)'
      }} data-id="element-1082">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1083">✅</span>
          <p className="text-white/85 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1084">
            You feel like culture is moving too fast, and something that
            genuinely mattered from your generation is fading into noise
          </p>
        </div>
        <div className="flex items-start gap-3 p-4 rounded-lg" style={{
        backgroundColor: 'rgba(239,68,68,0.07)',
        border: '1px solid rgba(239,68,68,0.2)'
      }} data-id="element-1085">
          <span className="text-xl flex-shrink-0 mt-0.5" data-id="element-1086">❌</span>
          <p className="text-white/70 text-sm md:text-base leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1087">
            This is NOT for you if you're looking for a plot recap, a "Did You
            Know?" trivia list, or anything you've already seen on a YouTube
            countdown
          </p>
        </div>
      </div>

      <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8 text-center italic" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1088">
        Most anniversary products treat you like a casual viewer. This one was
        built for the fan who already loves the film — and is ready to finally
        understand it.
      </p>

      <div className="text-center mb-8" data-id="element-1089">
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{
        color: ACCENT,
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1090">
          INTRODUCING
        </p>
        <h3 className="text-2xl md:text-3xl font-bold mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1091">
          BACK IN TIME
        </h3>
        <p className="text-white font-semibold text-lg md:text-xl mb-3" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1092">
          The 40th Anniversary Collector's Edition
        </p>
        <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-lg mx-auto" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1093">
          The most complete legacy document ever assembled for Back to the
          Future.
        </p>
      </div>

      <div className="flex justify-center" data-id="element-1094">
        <div className="relative w-full max-w-md" data-id="element-1095">
          <div className="absolute -inset-1 rounded-xl blur-md opacity-30" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1096" />
          <div className="relative rounded-xl overflow-hidden border border-orange-500/30 shadow-2xl" style={{
          aspectRatio: '1/1'
        }} data-id="element-1097">
            <img src="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png" alt="Back In Time 40th Anniversary Collector's Edition" className="w-full h-full object-cover" loading="lazy" decoding="async" width="500" height="500" data-id="element-1098" />
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── D. WHAT YOU GET STACK ────────────────────────────────────────────────────
const BOOK_IMAGES = ['https://cdn.magicpatterns.com/uploads/eRaLLFj1Xpd9yKh17SfpDw/BACK_IN_TIME_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/qWMGMZni8tSubnCQBBHeda/BEHIND_THE_CLOCK_TOWER_-_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/6N2oU2pwJLDZiEUA7vpeCu/GREAT_SCOTT_40_QUOTES_FOR_40_YEARS_COVER_DIGITAL.png', 'https://cdn.magicpatterns.com/uploads/8pvDLEzUAHUVNEPcPZcf74/TIME_IMAGINATION_DESTINY_-_COVER_DIGITAL.png'];
const BookImage = ({
  src,
  alt,
  onClick
}: {
  src: string;
  alt: string;
  onClick: () => void;
}) => <div className="flex-shrink-0 w-full md:w-[160px]" data-id="element-1099">
    <div className="relative cursor-pointer group mx-auto" style={{
    maxWidth: '160px'
  }} onClick={onClick} data-id="element-1100">
      <img src={src} alt={alt} className="rounded-lg w-full md:w-[160px] object-cover shadow-lg transition-opacity group-hover:opacity-80" style={{
      aspectRatio: '3/4',
      maxWidth: '160px'
    }} loading="lazy" decoding="async" width="160" height="213" data-id="element-1101" />
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-1102">
        <div className="bg-black/50 rounded-full p-1.5" data-id="element-1103">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1104">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1105" />
          </svg>
        </div>
      </div>
      <p className="text-center text-[9px] mt-1 leading-tight" style={{
      color: ACCENT,
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1106">
        Tap to enlarge
      </p>
    </div>
  </div>;
const WhatYouGetV4 = () => {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-1107">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1108">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-2 font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1109">
          What Makes This Different
        </h2>
        <p className="text-center text-white/50 text-sm mb-8" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1110">
          Four volumes. Every angle. Nothing left out.
        </p>

        <div className="space-y-6" data-id="element-1111">
          {/* Point 1 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-1112">
            <BookImage src={BOOK_IMAGES[0]} alt="Back In Time — The Cultural Analysis" onClick={() => setLightboxImage(BOOK_IMAGES[0])} data-id="element-1113" />
            <div className="flex-1" data-id="element-1114">
              <div className="flex items-center gap-2 mb-2" data-id="element-1115">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1116">
                  BOOK 1
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1117">
                IT DOCUMENTS THE LEGACY — NOT THE PLOT
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1118">
                190+ pages covering the cultural architecture of a story that's
                outlasted every trend, every franchise reboot, and every piece
                of technology it predicted. Why does it still feel alive? Why do
                complete strangers cry in the same place every time? Why do
                parents all over the world sit their kids down and press play
                like it's a rite of passage? This book answers all of it — with
                the depth the film has always deserved.
              </p>
            </div>
          </div>

          {/* Point 2 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-1119">
            <BookImage src={BOOK_IMAGES[1]} alt="Behind The Clock Tower" onClick={() => setLightboxImage(BOOK_IMAGES[1])} data-id="element-1120" />
            <div className="flex-1" data-id="element-1121">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1122">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1123">
                  BOOK 2
                </span>
                <span className="text-xs font-bold" style={{
                color: '#4ade80',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1124">
                  Retail Value:{' '}
                  <span className="line-through opacity-70" data-id="element-1125">$29.99</span> → FREE
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1126">
                THE PRODUCTION TRUTH NOBODY PUBLISHED PROPERLY
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1127">
                Michael J. Fox working 18-hour days — Family Ties from 10 AM to
                6 PM, then the BTTF set until 2 AM. The DeLorean that kept
                breaking down on night shoots, requiring industrial hairdryers
                to unfreeze its gull-wing doors between takes. The casting
                decision that nearly bankrupted the production and cost $4
                million to fix. It's all here, documented in full.
              </p>
            </div>
          </div>

          {/* Point 3 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-1128">
            <BookImage src={BOOK_IMAGES[2]} alt="Great Scott!" onClick={() => setLightboxImage(BOOK_IMAGES[2])} data-id="element-1129" />
            <div className="flex-1" data-id="element-1130">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1131">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1132">
                  BOOK 3
                </span>
                <span className="text-xs font-bold" style={{
                color: '#4ade80',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1133">
                  Retail Value:{' '}
                  <span className="line-through opacity-70" data-id="element-1134">$19.99</span> → FREE
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1135">
                40 VOICES. 40 YEARS. EVERY ONE COUNTS.
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1136">
                Not just the famous quotes. The layered ones. The ones that hit
                completely differently on the 20th viewing than they did on the
                first. Each one contextualized, with the real-world resonance
                documented behind it. The book you read once, then return to.
              </p>
            </div>
          </div>

          {/* Point 4 */}
          <div className="flex flex-col md:flex-row gap-5 items-start p-5 rounded-xl" style={{
          backgroundColor: '#111111',
          border: '1px solid rgba(255,108,0,0.15)'
        }} data-id="element-1137">
            <BookImage src={BOOK_IMAGES[3]} alt="Time. Imagination. Destiny." onClick={() => setLightboxImage(BOOK_IMAGES[3])} data-id="element-1138" />
            <div className="flex-1" data-id="element-1139">
              <div className="flex flex-col items-start gap-1 mb-2" data-id="element-1140">
                <span className="text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded" style={{
                backgroundColor: 'rgba(255,108,0,0.15)',
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1141">
                  BOOK 4
                </span>
                <span className="text-xs font-bold" style={{
                color: '#4ade80',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1142">
                  Retail Value:{' '}
                  <span className="line-through opacity-70" data-id="element-1143">$29.99</span> → FREE
                </span>
              </div>
              <h3 className="text-base md:text-lg font-bold text-white mb-2 font-future" data-id="element-1144">
                THE PHILOSOPHY THAT EXPLAINS WHY IT STILL HAUNTS YOU
              </h3>
              <p className="text-white/65 text-sm leading-relaxed" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1145">
                Why does a movie about a teenager in a time machine still feel
                this important 40 years later? This volume goes deeper than any
                film review ever has — exploring what Back to the Future says
                about free will, identity, ambition, and the courage required to
                change your future. The one you won't stop thinking about after
                you read it.
              </p>
            </div>
          </div>
        </div>
      </div>

      {lightboxImage && <ImageLightbox images={[lightboxImage]} startIndex={0} onClose={() => setLightboxImage(null)} data-id="element-1146" />}
    </section>;
};
// ─── E. PRICING + ORDER BUMPS (copied from V3) ─────────────────────────────
const PurchaseV4 = () => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [bump2Expanded, setBump2Expanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [bump1LightboxOpen, setBump1LightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const hasTrackedRef = useRef(false);
  const bump2Price = bump1 ? BUMP2_PRICE_WITH_BUMP1 : BUMP2_PRICE_STANDALONE;
  const total = BASE_PRICE + (bump1 ? BUMP1_PRICE : 0) + (bump2 ? bump2Price : 0);
  const shopPayInstallment = (Math.ceil(total / 4 * 100) / 100).toFixed(2);
  const checkoutUrl = buildUrl(bump2 ? CHECKOUT_BUNDLE : bump1 ? CHECKOUT_PREMIUM : CHECKOUT_DIGITAL);
  const handleCta = () => {
    const name = bump2 ? "Time Traveler's Collector Bundle" : bump1 ? 'Premium Hardcover Bundle' : 'Complete Digital Collection';
    trackV4(name, total);
  };
  const openLightbox = (e: React.MouseEvent, idx = 0) => {
    e.stopPropagation();
    setLightboxStart(idx);
    setLightboxOpen(true);
  };
  const scrollToCta = () => {
    if (window.innerWidth < 768 && ctaRef.current) {
      setTimeout(() => {
        ctaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 50);
    }
  };
  useEffect(() => {
    if (!sectionRef.current || hasTrackedRef.current) return;
    const isMobile = window.innerWidth < 768;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasTrackedRef.current) {
          hasTrackedRef.current = true;
          const delay = isMobile ? 1000 : 500;
          setTimeout(() => {
            const ctaTrigger = document.createElement('div');
            ctaTrigger.className = 'cta';
            ctaTrigger.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;top:0;left:0;';
            ctaTrigger.setAttribute('data-pricing-trigger', 'true');
            document.body.appendChild(ctaTrigger);
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true
            });
            ctaTrigger.dispatchEvent(clickEvent);
            if (isMobile) {
              const touchStartEvent = new TouchEvent('touchstart', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              const touchEndEvent = new TouchEvent('touchend', {
                bubbles: true,
                cancelable: true,
                view: window
              });
              ctaTrigger.dispatchEvent(touchStartEvent);
              ctaTrigger.dispatchEvent(touchEndEvent);
            }
            setTimeout(() => {
              if (ctaTrigger.parentNode) ctaTrigger.parentNode.removeChild(ctaTrigger);
            }, 2000);
          }, delay);
        }
      });
    }, {
      threshold: isMobile ? 0.1 : 0.3,
      rootMargin: '0px'
    });
    observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="pricing-section" style={{
    backgroundColor: BG,
    paddingTop: '32px',
    paddingBottom: '48px',
    scrollMarginTop: '80px'
  }} className="md:py-12" data-id="element-1147">
      <div className="container mx-auto px-4 md:px-10 max-w-2xl" data-id="element-1148">
        <div className="text-center mb-6 md:mb-10" data-id="element-1149">
          <span className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3" style={{
          backgroundColor: 'rgba(255,108,0,0.15)',
          color: ACCENT,
          border: '1px solid rgba(255,108,0,0.3)',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1150">
            🔥 40th Anniversary Launch Pricing
          </span>
          <h2 className="text-[22px] md:text-4xl font-bold text-center font-future tracking-wide bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" data-id="element-1151">
            Special 40th Anniversary Collector's Offer
          </h2>
          <p className="text-white/50 text-xs mt-2 max-w-sm mx-auto" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1152">
            ⚠️ This price exists because it's the 40th anniversary. When the
            window closes, it closes.
          </p>
        </div>

        {/* ── MAIN PRODUCT CARD ── */}
        <div className="rounded-xl mb-3 shadow-[0_8px_40px_rgba(0,0,0,0.7)]" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,108,0,0.2)'
      }} data-id="element-1153">
          {/* MOBILE */}
          <div className="md:hidden px-4 py-4" data-id="element-1154">
            <p className="text-[11px] font-bold text-center uppercase tracking-widest mb-3 font-future" style={{
            color: ACCENT,
            letterSpacing: '0.06em'
          }} data-id="element-1155">
              The Complete Digital Collection
            </p>
            <div className="flex gap-3 items-center mb-3" data-id="element-1156">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-lg shadow-md flex-shrink-0" style={{
              width: '100px',
              height: '100px',
              objectFit: 'cover'
            }} loading="lazy" decoding="async" width="100" height="100" data-id="element-1157" />
              <div className="flex-1 grid grid-cols-1 gap-y-1" data-id="element-1158">
                {['Back In Time (190 pg)', 'Behind The Clock Tower (87 pg) — FREE', 'Great Scott! (87 pg) — FREE', 'Time. Imagination. Destiny. (71 pg) — FREE'].map(item => <div key={item} className="flex items-start gap-1.5" data-id="element-1159">
                    <span style={{
                  color: ACCENT,
                  fontSize: '8px',
                  flexShrink: 0,
                  marginTop: '4px'
                }} data-id="element-1160">
                      ✦
                    </span>
                    <span className="text-[11px] leading-tight" style={{
                  color: '#F5F5F5',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-1161">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>
            <div className="text-center mb-2" data-id="element-1162">
              <span className="line-through text-sm opacity-40 mr-2" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1163">
                $139.94
              </span>
              <span className="font-bold text-[26px] leading-none transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1164">
                ${total.toFixed(2)}
              </span>
              {(bump1 || bump2) && <span className="block text-[10px] text-white/40 mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1165">
                  w/ selected add-ons
                </span>}
            </div>
            <div className="text-center mb-2" data-id="element-1166">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-2.5 py-1 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1167">
                You Save ${(139.94 - total).toFixed(2).replace('-', '')}
              </span>
            </div>
            {/* Shop Pay installments */}
            <div className="mb-3 px-3 py-2 bg-black/30 rounded-lg border border-white/10 text-center" data-id="element-1168">
              <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-1169">
                <span className="text-gray-300 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1170">
                  or 4 payments of
                </span>
                <span className="font-bold text-sm" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1171">
                  ${shopPayInstallment}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5" data-id="element-1172">
                <span className="text-gray-400 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1173">
                  with
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-1174" />
              </div>
              <p className="text-gray-400 text-[10px] mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1175">
                Biweekly interest-free payments
              </p>
            </div>
            <div ref={ctaRef} data-id="element-1176">
              <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1177">
                GET MY COLLECTION →
              </OrangeButton>
            </div>
            <p className="text-center text-white/40 text-[10px] mt-1.5" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1178">
              Instant delivery. Readable on any device.
            </p>
          </div>

          {/* DESKTOP */}
          <div className="hidden md:block p-10" data-id="element-1179">
            <h3 className="text-2xl font-bold text-center mb-6 font-future tracking-wide" style={{
            color: '#F5F5F5',
            letterSpacing: '0.04em'
          }} data-id="element-1180">
              THE COMPLETE DIGITAL COLLECTION
            </h3>
            <div className="flex justify-center mb-6" data-id="element-1181">
              <img src="https://cdn.magicpatterns.com/uploads/drsCGCcawJQmxdkJUsQyKs/Holiday_Edition_Digital_Set.png" alt="Complete Digital Collection" className="rounded-xl shadow-md" style={{
              maxWidth: '260px',
              width: '100%'
            }} loading="lazy" decoding="async" width="260" height="260" data-id="element-1182" />
            </div>
            <ul className="space-y-3 mb-6" data-id="element-1183">
              {['Back In Time (190 pages) — The Cultural Analysis', 'Behind the Clock Tower (87 pages) — The Production Story — FREE', 'Great Scott! (87 pages) — 40 Reflections for 40 Years — FREE', 'Time. Imagination. Destiny. (71 pages) — The Philosophical Legacy — FREE'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-1184">
                  <span className="font-bold mt-0.5" style={{
                color: ACCENT,
                flexShrink: 0
              }} data-id="element-1185">
                    ✦
                  </span>
                  <span className="text-base leading-relaxed" style={{
                color: '#F5F5F5',
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1186">
                    {item}
                  </span>
                </li>)}
            </ul>
            <div className="text-center mb-2" data-id="element-1187">
              <span className="line-through opacity-40 mr-2 text-lg" style={{
              color: '#F5F5F5',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1188">
                $139.94
              </span>
              <span className="font-bold text-3xl transition-all duration-300 bg-gradient-to-r from-yellow-300 to-orange-500 text-transparent bg-clip-text" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1189">
                ${total.toFixed(2)}
              </span>
            </div>
            {(bump1 || bump2) && <p className="text-center text-xs mb-2 text-white/40" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1190">
                Includes selected add-ons
              </p>}
            <div className="text-center mb-3" data-id="element-1191">
              <span className="inline-flex items-center gap-1 bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-bold px-3 py-1.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1192">
                You Save ${(139.94 - total).toFixed(2).replace('-', '')}
              </span>
            </div>
            {/* Shop Pay installments */}
            <div className="mb-4 px-4 py-2.5 bg-black/30 rounded-lg border border-white/10 text-center max-w-xs mx-auto" data-id="element-1193">
              <div className="flex items-center justify-center gap-2 mb-0.5" data-id="element-1194">
                <span className="text-gray-300 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1195">
                  or 4 payments of
                </span>
                <span className="font-bold text-sm" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1196">
                  ${shopPayInstallment}
                </span>
              </div>
              <div className="flex items-center justify-center gap-1.5" data-id="element-1197">
                <span className="text-gray-400 text-xs" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1198">
                  with
                </span>
                <img src="https://cdn.magicpatterns.com/uploads/sSzxmJmvJ5ddbjuz1Lh65R/Shop_Pay_logo_purple.png" alt="Shop Pay" className="h-5 inline-block" width="60" height="20" loading="lazy" decoding="async" data-id="element-1199" />
              </div>
              <p className="text-gray-400 text-[10px] mt-0.5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1200">
                Biweekly interest-free payments
              </p>
            </div>
            <OrangeButton href={checkoutUrl} onClick={handleCta} data-id="element-1201">
              GET MY COLLECTION →
            </OrangeButton>
            <p className="text-center text-white/40 text-xs mt-3" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1202">
              Instant delivery. Readable on any device.
            </p>
            {/* Trust icons */}
            <div className="flex items-center justify-center gap-5 mt-4" data-id="element-1203">
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1204">
                <ShieldCheckIcon className="h-4 w-4 text-green-500" data-id="element-1205" /> Secure
                Checkout
              </div>
              <div className="flex items-center gap-1.5 text-white/40 text-xs" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1206">
                <LockIcon className="h-4 w-4 text-orange-400" data-id="element-1207" /> One-Time
                Payment
              </div>
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 1 ── */}
        <div className="rounded-xl mb-3 overflow-hidden transition-all duration-300" style={{
        border: bump1 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump1 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump1 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1208">
          <div className="px-4 py-2" style={{
          backgroundColor: bump1 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1209">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1210">
              ⚡ Special One-Time Offer
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1211">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-1212">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump1 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1213">
                {bump1 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1214" />}
              </div>
              <div data-id="element-1215">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1216">
                  Add the Premium Hardcover Edition{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1217">
                    — Only +${BUMP1_PRICE} Today
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1218">
                  Own it. Display it. Keep it forever.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-3" data-id="element-1219">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => {
              e.stopPropagation();
              setBump1LightboxOpen(true);
            }} data-id="element-1220">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1221">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png" alt="Premium Hardcover Edition" className="rounded-lg shadow-lg w-full h-full object-cover transition-opacity group-hover:opacity-80" loading="lazy" decoding="async" width="130" height="130" data-id="element-1222" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" data-id="element-1223">
                    <div className="bg-black/50 rounded-full p-1.5" data-id="element-1224">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-1225">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" data-id="element-1226" />
                      </svg>
                    </div>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1227">
                  Tap to enlarge
                </p>
              </div>
              <ul className="space-y-1.5 flex-1" data-id="element-1228">
                {BUMP1_ITEMS.map(item => <BumpItem key={item} text={item} data-id="element-1229" />)}
              </ul>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none" style={{
            backgroundColor: bump1 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump1 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const n = !bump1;
            setBump1(n);
            if (n) scrollToCta();
          }} data-id="element-1230">
              {bump1 ? '✓ Added to my order' : `Add Premium Hardcover — Only +$${BUMP1_PRICE}`}
            </div>
          </div>
        </div>

        {/* ── ORDER BUMP 2 ── */}
        <div className="rounded-xl mb-5 overflow-hidden transition-all duration-300" style={{
        border: bump2 ? `3px solid ${ACCENT}` : '3px solid rgba(255,108,0,0.4)',
        backgroundColor: bump2 ? 'rgba(255,108,0,0.07)' : '#111111',
        boxShadow: bump2 ? `0 0 28px ${ACCENT_GLOW}` : '0 2px 12px rgba(0,0,0,0.5)'
      }} data-id="element-1231">
          <div className="px-4 py-2" style={{
          backgroundColor: bump2 ? ACCENT : 'rgba(255,108,0,0.18)'
        }} data-id="element-1232">
            <span className="text-xs font-bold uppercase tracking-widest" style={{
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1233">
              🚀 Ultimate Upgrade
            </span>
          </div>
          <div className="px-4 pt-3 pb-4" data-id="element-1234">
            <div className="flex items-start gap-3 mb-3 cursor-pointer" onClick={() => {
            const n = !bump2;
            setBump2(n);
            if (n) scrollToCta();
          }} data-id="element-1235">
              <div className="flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200" style={{
              borderColor: ACCENT,
              backgroundColor: bump2 ? ACCENT : 'transparent',
              minWidth: '24px',
              marginTop: '1px'
            }} data-id="element-1236">
                {bump2 && <CheckIcon className="h-4 w-4" style={{
                color: '#0B0B0B'
              }} data-id="element-1237" />}
              </div>
              <div data-id="element-1238">
                <p className="font-bold text-white text-sm md:text-base leading-snug" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1239">
                  Upgrade to the Complete Collector's Bundle{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-1240">
                    — Only +${bump2Price} Today
                    {bump1 && <span className="ml-2 text-xs font-normal text-white/35 line-through" data-id="element-1241">
                        ${BUMP2_PRICE_STANDALONE}
                      </span>}
                  </span>
                </p>
                <p className="text-white/50 text-xs mt-0.5 italic" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1242">
                  Every volume. Every format. The full archive.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-start mb-2" data-id="element-1243">
              <div className="flex-shrink-0 cursor-pointer group" onClick={e => openLightbox(e, 0)} data-id="element-1244">
                <div className="relative w-[100px] h-[100px] md:w-[130px] md:h-[130px]" data-id="element-1245">
                  <img src={BUNDLE_IMAGES[0]} alt="Time Traveler's Collector Bundle" className="rounded-lg shadow-lg transition-opacity group-hover:opacity-80 w-full h-full object-cover" loading="lazy" decoding="async" width="130" height="130" data-id="element-1246" />
                  <div className="absolute bottom-1 right-1 bg-black/70 rounded px-1 py-0.5 flex gap-0.5" data-id="element-1247">
                    {[1, 2, 3].map(i => <div key={i} className="rounded-sm overflow-hidden" style={{
                    width: '12px',
                    height: '12px'
                  }} data-id="element-1248">
                        <img src={BUNDLE_IMAGES[i]} alt="" className="w-full h-full object-cover" decoding="async" loading="lazy" data-id="element-1249" />
                      </div>)}
                    <span className="text-white text-[8px] ml-0.5 leading-none self-center" data-id="element-1250">
                      +{BUNDLE_IMAGES.length - 4}
                    </span>
                  </div>
                </div>
                <p className="text-center text-[9px] mt-1 leading-tight w-[100px] md:w-[130px]" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-1251">
                  Tap to see all items
                </p>
              </div>
              <div className="flex-1" data-id="element-1252">
                <ul className="space-y-1.5" data-id="element-1253">
                  {BUMP2_ITEMS.slice(0, bump2Expanded ? BUMP2_ITEMS.length : 4).map(item => <BumpItem key={item} text={item} data-id="element-1254" />)}
                </ul>
                {BUMP2_ITEMS.length > 4 && <button type="button" className="flex items-center gap-1 text-[11px] mt-1.5" style={{
                color: ACCENT,
                fontFamily: 'Inter, sans-serif',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} onClick={e => {
                e.stopPropagation();
                setBump2Expanded(!bump2Expanded);
              }} data-id="element-1255">
                    <ChevronDownIcon className={`h-3 w-3 transition-transform ${bump2Expanded ? 'rotate-180' : ''}`} data-id="element-1256" />
                    {bump2Expanded ? 'Show less' : `+${BUMP2_ITEMS.length - 4} more items`}
                  </button>}
              </div>
            </div>
            <div className="w-full text-center font-bold py-3 rounded-lg transition-all duration-200 text-sm cursor-pointer select-none mt-2" style={{
            backgroundColor: bump2 ? ACCENT : 'transparent',
            border: `2px solid ${ACCENT}`,
            color: bump2 ? '#0B0B0B' : ACCENT,
            fontFamily: 'Inter, sans-serif'
          }} onClick={() => {
            const n = !bump2;
            setBump2(n);
            if (n) scrollToCta();
          }} data-id="element-1257">
              {bump2 ? '✓ Upgrade added' : `Upgrade to Collector Bundle — Only +$${bump2Price}`}
            </div>
          </div>
        </div>

        {/* ── FALLBACK ── */}
        <div className="rounded-xl py-4 px-5 text-center" style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1258">
          <p className="text-white/45 text-xs italic mb-3" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1259">
            Just want the core retrospective?
          </p>
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-1260">
            <img src="https://cdn.magicpatterns.com/uploads/ewSrEYsf3d6aAxsfRJbJ5B/BACK_IN_TIME_-_DIGITAL11v2.png" alt="Digital Edition" className="rounded-md" style={{
            width: '52px',
            height: '52px',
            objectFit: 'cover',
            opacity: 0.6
          }} loading="lazy" decoding="async" width="52" height="52" data-id="element-1261" />
            <div className="text-left" data-id="element-1262">
              <p className="text-white/55 text-sm font-medium" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1263">
                Digital Edition
              </p>
              <p className="text-white/45 text-sm" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1264">
                $39.97
              </p>
            </div>
          </div>
          <a href={buildUrl(CHECKOUT_BASIC)} onClick={() => trackV4('Digital Edition', 39.97)} className="text-white/40 text-xs underline hover:text-white/55 transition-colors" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1265">
            Get the Digital Edition
          </a>
        </div>
      </div>

      {lightboxOpen && <ImageLightbox images={BUNDLE_IMAGES} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} data-id="element-1266" />}
      {bump1LightboxOpen && <ImageLightbox images={['https://uploadthingy.s3.us-west-1.amazonaws.com/pSD1kcciQ8RbkUdnckm8uJ/PREMIUM_EDITION_V1.2.png']} startIndex={0} onClose={() => setBump1LightboxOpen(false)} data-id="element-1267" />}
    </section>;
};
// ─── F. SOCIAL PROOF ─────────────────────────────────────────────────────────
const TestimonialsV4 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1268">
    <div className="container mx-auto px-4 max-w-4xl" data-id="element-1269">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 font-future tracking-wide text-white" data-id="element-1270">
        What Real Fans Are Saying
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1271">
        {/* Testimonial 1 */}
        <div className="p-5 rounded-xl flex flex-col" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1272">
          <div className="flex gap-0.5 mb-3" data-id="element-1273">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1274" />)}
          </div>
          <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1275">
            "I've collected BTTF merchandise for 20 years. Steelbooks, prints,
            the whole thing. This is the first time I've read something about
            this film that made me feel like the person who made it actually
            understood why it matters. Read the whole flagship book in one
            night."
          </p>
          <div data-id="element-1276">
            <p className="text-white font-semibold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1277">
              Mark T., 47
            </p>
            <p className="text-white/40 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1278">
              Columbus, Ohio
            </p>
          </div>
        </div>

        {/* Testimonial 2 */}
        <div className="p-5 rounded-xl flex flex-col" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1279">
          <div className="flex gap-0.5 mb-3" data-id="element-1280">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1281" />)}
          </div>
          <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1282">
            "My son is 13. He'd seen the trilogy twice but didn't really get why
            I treat it like it's sacred. I read him the section on why fathers
            show this film to their kids. He went quiet for a second and then
            said 'that's literally us right now, Dad.' That's worth more than
            any price tag."
          </p>
          <div data-id="element-1283">
            <p className="text-white font-semibold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1284">
              Sandra R., 52
            </p>
            <p className="text-white/40 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1285">
              Austin, Texas
            </p>
          </div>
        </div>

        {/* Testimonial 3 */}
        <div className="p-5 rounded-xl flex flex-col" style={{
        backgroundColor: '#111111',
        border: '1px solid rgba(255,108,0,0.15)'
      }} data-id="element-1286">
          <div className="flex gap-0.5 mb-3" data-id="element-1287">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-1288" />)}
          </div>
          <p className="text-white/75 text-sm leading-relaxed mb-4 flex-1 italic" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1289">
            "I went in skeptical, every 'definitive' BTTF product I've bought
            has been gorgeous packaging with nothing underneath. Behind The
            Clock Tower alone destroyed that pattern. The DeLorean hairdryer
            story, the Stoltz reshoot, the 18-hour Fox schedule, I've been a fan
            for 35 years and I didn't know any of it at this level of detail."
          </p>
          <div data-id="element-1290">
            <p className="text-white font-semibold text-sm" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1291">
              David M., 39
            </p>
            <p className="text-white/40 text-xs" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1292">
              San Diego, California
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// ─── G. VALUE SUMMARY ────────────────────────────────────────────────────────
const ValueSummaryV4 = () => <section className="py-8 md:py-12" style={{
  backgroundColor: BG
}} data-id="element-1293">
    <div className="container mx-auto px-4 max-w-2xl" data-id="element-1294">
      <div className="rounded-xl overflow-hidden" style={{
      border: `2px solid ${GOLD}`,
      backgroundColor: '#0d0d0d'
    }} data-id="element-1295">
        <div className="px-5 py-3 text-center" style={{
        backgroundColor: 'rgba(212,175,55,0.1)'
      }} data-id="element-1296">
          <span className="text-xs font-bold uppercase tracking-widest" style={{
          color: GOLD,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-1297">
            Total Value Summary
          </span>
        </div>
        <div className="p-5 md:p-8" data-id="element-1298">
          <div className="space-y-3 mb-6" data-id="element-1299">
            <div className="flex justify-between items-center text-sm md:text-base" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1300">
              <span className="text-white/70" data-id="element-1301">
                Back In Time: 40th Anniversary Collector's Edition
              </span>
              <span className="text-white font-semibold" data-id="element-1302">$59.97</span>
            </div>
            <div className="flex justify-between items-center text-sm md:text-base" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1303">
              <span className="text-white/70" data-id="element-1304">
                FREE: Behind The Clock Tower
              </span>
              <span style={{
              color: GOLD
            }} className="font-semibold" data-id="element-1305">
                $29.99
              </span>
            </div>
            <div className="flex justify-between items-center text-sm md:text-base" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1306">
              <span className="text-white/70" data-id="element-1307">
                FREE: Great Scott! 40 Quotes For 40 Years
              </span>
              <span style={{
              color: GOLD
            }} className="font-semibold" data-id="element-1308">
                $19.99
              </span>
            </div>
            <div className="flex justify-between items-center text-sm md:text-base" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-1309">
              <span className="text-white/70" data-id="element-1310">
                FREE: Time, Imagination & Destiny
              </span>
              <span style={{
              color: GOLD
            }} className="font-semibold" data-id="element-1311">
                $29.99
              </span>
            </div>
            <div className="border-t pt-3 mt-3 flex justify-between items-center" style={{
            borderColor: 'rgba(212,175,55,0.3)'
          }} data-id="element-1312">
              <span className="text-white font-bold text-sm md:text-base" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1313">
                TOTAL VALUE
              </span>
              <span className="text-white font-bold line-through opacity-50 text-sm md:text-base" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1314">
                $139.94
              </span>
            </div>
            <div className="flex justify-between items-center pt-2" data-id="element-1315">
              <span className="font-bold text-lg md:text-xl text-white" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1316">
                YOUR PRICE TODAY
              </span>
              <span className="font-bold text-3xl md:text-4xl" style={{
              color: ACCENT,
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-1317">
                $59.97
              </span>
            </div>
          </div>
          <OrangeButton href="#pricing-section" data-id="element-1318">
            GET MY COLLECTION — $59.97 →
          </OrangeButton>
        </div>
      </div>
    </div>
  </section>;
// ─── H. FAQ (inline, with commas instead of em-dashes) ───────────────────────
const faqItemsV4 = [{
  question: 'What formats are the books available in?',
  answer: 'The books are available in digital (PDF, instant download) and physical (premium hardcover or paperback, printed on demand). Some bundles include both formats. Digital editions are compatible with all devices, phones, tablets, computers, and e-readers.'
}, {
  question: 'How quickly will I receive my order?',
  answer: "Digital editions are delivered instantly on the thank-you page after checkout and also sent to your email.\n\nPhysical books are professionally printed on demand and typically arrive within 9,14 days. The Time Traveler's Collector Bundle ships in 8,10 business days. Once your order ships, you'll receive a tracking email automatically."
}, {
  question: 'Is this official/licensed content?',
  answer: 'No. This is an independent fan publication created to celebrate film history and cultural impact. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" is a trademark of Universal City Studios LLC.\n\nAll original written material, design, commentary, and analysis are protected under copyright as original works of authorship owned by Legendary Reads.'
}, {
  question: 'What makes this different from other BTTF books?',
  answer: "Because it wasn't made to be fast or disposable.\n\nThis collection took two years of deep research, careful writing, multiple revisions, and a clear editorial vision. It is not recycled Wikipedia content, not AI-generated filler, and not a coffee table book with photos you've seen a thousand times.\n\nIt's the comprehensive deep-dive true fans have been waiting for."
}, {
  question: 'Why only 1,985 copies?',
  answer: "1985 is the year Back to the Future was released. The physical edition is limited to exactly 1,985 copies as a tribute to that moment, making each copy a genuine collector's item. Once they're gone, they won't be reprinted."
}, {
  question: 'Can I gift this to someone?',
  answer: "Yes. Simply enter the recipient's shipping address at checkout for physical editions. For digital editions, the download link is sent to the email used at checkout, you can forward it to the recipient or purchase using their email address."
}, {
  question: 'Are there future updates or additions?',
  answer: 'Digital edition buyers may receive updates if new content is added to the collection. Physical editions are fixed at the time of printing. We recommend joining our email list to stay informed about any future releases or companion volumes.'
}, {
  question: 'Who wrote this book?',
  answer: 'This collection was written by The VHS Club, a dedicated team of writers and researchers who spent years researching, organizing, and refining the content.\n\nThis was not rushed, outsourced, or assembled from recycled material, it was created intentionally for true fans.'
}, {
  question: 'Is this book generated entirely by AI?',
  answer: 'No.\n\nAll research, writing, analysis, and structure were created by humans over an extended period of time. AI tools were only used in limited ways, such as formatting, organization, and final layout assistance.\n\nAI was used as a tool, not as the author.'
}, {
  question: 'Is there an audiobook version?',
  answer: "Yes. The Back In Time Audiobook Experience is included exclusively in the Time Traveler's Collector Bundle. It's a professionally produced audio version of the main book, perfect for listening on the go."
}, {
  question: 'What if I have an issue with my order?',
  answer: "Please contact us directly at info.legendaryreads@gmail.com and we'll be happy to help.\n\nFor the fastest resolution, we always recommend reaching out to us before contacting your bank or card provider."
}];
const FAQItemV4 = ({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) => <div className="border rounded-lg overflow-hidden transition-colors duration-200" style={{
  borderColor: 'rgba(255,108,0,0.2)'
}} data-id="element-1319">
    <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors duration-200 cursor-pointer" style={{
    backgroundColor: 'rgba(17,17,17,0.8)'
  }} aria-expanded={isOpen} data-id="element-1320">
      <span className="text-base font-semibold text-white pr-4" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-1321">
        {question}
      </span>
      <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} style={{
      color: ACCENT
    }} data-id="element-1322" />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1323">
      <div className="px-5 pb-5 pt-1" style={{
      backgroundColor: 'rgba(11,11,11,0.6)'
    }} data-id="element-1324">
        {answer.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm leading-relaxed mb-3 last:mb-0" style={{
        color: 'rgba(245,245,245,0.6)',
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-1325">
            {paragraph}
          </p>)}
      </div>
    </div>
  </div>;
const FAQV4 = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section className="py-8 md:py-12" style={{
    backgroundColor: BG
  }} data-id="element-1326">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-1327">
        <div className="text-center mb-8" data-id="element-1328">
          <h2 className="text-2xl md:text-3xl font-bold font-future text-white mb-2" data-id="element-1329">
            Questions? We've Got Answers.
          </h2>
          <div className="w-20 h-1 mx-auto rounded-full" style={{
          background: 'linear-gradient(90deg, #FFD300, #FF6C00)'
        }} data-id="element-1330" />
        </div>
        <div className="space-y-3" data-id="element-1331">
          {faqItemsV4.map((item, index) => <FAQItemV4 key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} onToggle={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-1332" />)}
        </div>
      </div>
    </section>;
};
// ─── LANDING V4 EXPORT ────────────────────────────────────────────────────────
export const LandingV4 = () => <div className="w-full min-h-screen" style={{
  backgroundColor: BG,
  color: '#F5F5F5'
}} data-id="element-1333">
    <AlertBar data-id="element-1334" />
    <HeaderV4 data-id="element-1335" />
    <main data-id="element-1336">
      <HeroV4 data-id="element-1337" />
      <IdentificationV4 data-id="element-1338" />
      <WhatYouGetV4 data-id="element-1339" />
      <PurchaseV4 data-id="element-1340" />
      <TestimonialsV4 data-id="element-1341" />
      <ValueSummaryV4 data-id="element-1342" />
      <FAQV4 data-id="element-1343" />
    </main>
    <Footer data-id="element-1344" />
  </div>;